import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { isMobile } from '../../../util/src/public-api';
import { ActionRecord } from './../../../model/src/lib/action-record.model';
import { CompanyDetail } from './../../../model/src/lib/company-detail.model';
import { Department } from './../../../model/src/lib/department.model';
import { MenuTree } from './../../../model/src/lib/menu-tree.model';
import { PersonnelPermission } from './../../../model/src/lib/personnel-permission.model';
import { System } from './../../../model/src/lib/system.model';
import { UserProfile } from './../../../model/src/lib/user-profile.model';
import { AppConfig } from './../../config/app.config';
import { ApiBaseService } from './api-base.service';
import { UserProfileService } from './user-profile.service';

@Injectable({
  providedIn: 'root',
})
export class ApiRbacService extends ApiBaseService {
  constructor(
    protected appConfig: AppConfig,
    protected http: HttpClient,
    private userProfileService: UserProfileService) {
    super(appConfig.getApiUrl('rbac'), http);
  }

  /**
   * @description 查詢 指定公司 之 所有部門
   *
   * @param companyId 公司代碼
   * @returns 所有部門資料
   */
  getAllDepartmentByCompanyId(companyId: string): Observable<Department[]> {
    return this.doGet(`/Department/Company/${companyId}`);
  }

  /**
   * @description 查詢 指定部門 或包含 所有下屬部門 之 所有用戶(主職)
   *
   * @param departmentId 部門代碼
   * @param isSub 是否包含下屬部門
   * @returns 所有用戶(主職)資料
   */
  getUserProfilesByDepartmentId(
    departmentId: string,
    isSub = false
  ): Observable<UserProfile[]> {
    return this.doGet(`/UserProfile/Department/${departmentId}/${isSub}`);
  }

  /**
   * @description 查詢用戶資料
   *
   * @param userNo 用戶帳號
   * @returns 用戶資料
   */
  getUserProfilesByUserNo(userNo: string): Observable<UserProfile> {
    return this.doGet(`/UserProfile/${userNo}`);
  }

  /**
   * @description 取得所有公司資料
   * @returns 所有公司資料
   */
  getCompanys(): Observable<CompanyDetail[]> {
    return this.doGet<CompanyDetail[]>('/Company').pipe(
      switchMap((response) => {
        return of(
          response.map((item) => {
            const { companyId, ...others } = item;
            return {
              companyId: item.companyId.toString(),
              ...others,
            };
          })
        );
      })
    );
  }

  /**
   * @description 查詢UserNo是否為主管
   */
  isSupervisorByUserNo(userNo: string, filterVirtual?: boolean): Observable<boolean> {
    const params = (filterVirtual !== null && filterVirtual !== undefined) ? `?filterVirtual=${filterVirtual}` : '';
    return this.doGet<
      { approvedMgrList: { authenticationUserNo: string }[] }[]
    >(`/UserProfile/ApprovedMgrList/${userNo}${params}`).pipe(
      map((res) => {
        if (res) {
          const authUserNos = res.reduce((pre, curr) => {
            return [
              ...pre,
              ...curr.approvedMgrList.map((item) => item.authenticationUserNo),
            ];
          }, [] as string[]);
          return authUserNos.includes(userNo);
        } else {
          return false;
        }
      })
    );
  }

  /**
   * @description 查詢所有系統
   */
  getSystems(): Observable<System[]> {
    return this.doGet<System[]>('/System');
  }

  /**
   * @description 取得使用者的系統選單
   * @param userNo User No
   * @param systemGroupCode System group code
   * @returns menu 資料
   */
  getMenuTrees(
    userNo: string,
    systemGroupCode: string
  ): Observable<MenuTree[]> {
    return this.doGet<MenuTree[]>(
      `/UserProfile/${userNo}/SystemGroup/${systemGroupCode}/MenuTree`
    );
  }

  /**
   * @description 查詢資料權限
   * @param params 查詢條件包含 userNo, functionCode, privilegeCode, companyId, workType 資料
   * @returns 資料權限
   */
  getPersonnelPermission(params: {
    userNo: string;
    functionCode: string;
    privilegeCode: string;
    companyId: number;
    workType: number;
  }): Observable<PersonnelPermission> {
    return this.doPost<PersonnelPermission>('/JointPrivilege/Search', params);
  }


  /**
   * 新增紀錄Log
   *
   * @param {ActionRecord} params
   * @return {*}  {Observable<any>}
   * @memberof ApiRbacService
   */
  actionRecord(paramsFromDomain: {
    systemId: number;
    systemFunctionId: number;
    domain?: string;
    action?: string;
    caseNo?: string;
  }): Observable<any> {
    const userNo = this.userProfileService.currentUserValue?.userNo ?? '';
    const params = {
      ...paramsFromDomain,
      isMobile: isMobile(),
      createUserNo: userNo,
      createTime: new Date()
    };
    return this.doPost<ActionRecord>('/Action/Record', params);
  }
}
